import { useRef, useState } from 'react'

import { BorderHeading, Text } from '@hhgtech/hhg-components/components'
import { useIntl } from 'react-intl'

import { domainLocales } from 'domainLocales'
import { useAnimatedHeightToggle } from 'hooks/useAnimatedHeightToggle'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, basePath } from 'interfaces/constants'
import { FAQs } from 'interfaces/types'

import {
  StyledCollapsibleFaqs,
  StyledFaqsContent,
  StyledFaqsHeading,
} from './index.styled'

type Props = {
  isMobile?: boolean
  posts: Array<FAQs>
  title?: string
}

type PropsCard = {
  isMobile?: boolean
  postContent: FAQs
  onShowClick?: (action: 'show' | 'hide') => void
  isShowed?: boolean
}

const CollapsibleFaqsCard = ({
  postContent,
  onShowClick,
  isShowed,
}: PropsCard) => {
  const content = useRef<HTMLDivElement>(null)
  const { formatMessage: f } = useIntl()

  useAnimatedHeightToggle(content, isShowed)

  const handleShow = () => {
    typeof onShowClick !== 'undefined' &&
      onShowClick(!isShowed ? 'show' : 'hide')
  }
  const {
    locale,
    router: { asPath },
  } = useRouterExtend()

  return (
    <>
      <StyledFaqsHeading onClick={handleShow}>
        <Text size="lg" type="bold" className="heading">
          {postContent.title}
        </Text>
        <div className="icon" data-is-hidden={!isShowed}>
          <img
            loading="lazy"
            src={`${assetImagePath}/chevronDown.svg`}
            alt="Icon Chevron"
            data-event-category="FAQ"
            data-event-action="Arrow Click"
            data-event-label={`https://${domainLocales[locale]}${basePath}${asPath}`}
          />
        </div>
      </StyledFaqsHeading>

      <StyledFaqsContent ref={content}>
        <div className="content-wrapper">
          <div className="content">
            <Text size="c1">{postContent.content} </Text>
          </div>
          {postContent.learnMore ? (
            <div className="learn-more">
              <Text size="c1" type="bold">
                {`${f({
                  id: 'categoryPage.faqs.learnMore',
                  defaultMessage: 'Learn More',
                })}: `}
              </Text>
              <a href={postContent.learnMore.permalink}>
                <Text size="c1" color="#2685f1">
                  <span
                    data-event-category="FAQ"
                    data-event-action="Internal Link Click"
                    data-event-label={`https://${domainLocales[locale]}${basePath}${postContent.learnMore.permalink}`}
                    dangerouslySetInnerHTML={{
                      __html: postContent?.learnMore?.title,
                    }}
                  />
                </Text>
              </a>
            </div>
          ) : null}
        </div>
      </StyledFaqsContent>
    </>
  )
}

const CollapsibleFaqs = ({ isMobile, posts, title }: Props) => {
  const { formatMessage: f } = useIntl()
  const [currentShowing, setCurrentShowing] = useState<number>(-1)

  const handleShowFAQ = (type: 'show' | 'hide', index: number) => {
    setCurrentShowing(type === 'show' ? index : -1)
  }

  return (
    <StyledCollapsibleFaqs>
      <BorderHeading size="lg" className="faq-title">
        {title ||
          f({
            id: 'categoryPage.faqs',
            defaultMessage: 'FAQs',
          })}
      </BorderHeading>

      <div>
        {posts.map((post, index) => (
          <CollapsibleFaqsCard
            key={index}
            isMobile={isMobile}
            postContent={post}
            onShowClick={(type) => {
              handleShowFAQ(type, index)
            }}
            isShowed={currentShowing === index}
          />
        ))}
      </div>
    </StyledCollapsibleFaqs>
  )
}

export { CollapsibleFaqs }
